import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { FN } from "./facilityNetworkTheme";

type MetricCardProps = {
  label: string;
  value: string;
  progress: number;
};

function ComplianceMetricCard({ label, value, progress }: MetricCardProps) {
  const clamped = Math.min(100, Math.max(0, progress));

  return (
    <Box
      sx={{
        flex: { xs: "1 1 100%", md: "1 1 0" },
        minWidth: 0,
        height: 89,
        bgcolor: "#f0fbfd",
        border: `1px solid ${FN.metricCardBorder}`,
        borderRadius: "8px",
        px: 2.5,
        py: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#475467", mb: 0.25 }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 500,
          color: FN.primary,
          lineHeight: 1.4,
          mb: 0.5,
        }}
      >
        {value}
      </Typography>
      <Box
        sx={{
          height: 7,
          bgcolor: "#fff",
          borderRadius: "22px",
          boxShadow: "0px 0px 2.4px rgba(0,0,0,0.24)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${clamped}%`,
            borderRadius: "22px",
            background: "linear-gradient(90deg, #0a829d 0%, #58c2d8 100%)",
          }}
        />
      </Box>
    </Box>
  );
}

type Props = {
  facilityName: string;
  lastAnalyzedLabel?: string | null;
  complianceScore?: number | null;
  openItems?: number | null;
  overdueCount?: number | null;
  loading?: boolean;
};

/** Figma node 8503:64184 — Facility compliance overview */
export default function FacilityComplianceOverviewSection({
  facilityName,
  lastAnalyzedLabel,
  complianceScore,
  openItems,
  overdueCount,
  loading = false,
}: Props) {
  const subtitle = lastAnalyzedLabel
    ? `${facilityName} — last analyzed ${lastAnalyzedLabel}`
    : facilityName;

  return (
    <Box
      sx={{
        bgcolor: "#f8fdfe",
        border: "1px solid #cff7ff",
        borderRadius: "8px",
        p: 1,
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <DescriptionOutlinedIcon sx={{ fontSize: 20, color: FN.pressed, mt: 0.25 }} />
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 500, color: FN.pressed, fontFamily: FN.fontFamily }}>
            Facility compliance overview
          </Typography>
          <Typography sx={{ fontSize: 14, color: FN.pressed, fontFamily: FN.fontFamily }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} sx={{ color: FN.primary }} />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 1.5,
          }}
        >
          <ComplianceMetricCard
            label="Compliance score"
            value={complianceScore != null ? `${complianceScore}%` : "—"}
            progress={complianceScore ?? 0}
          />
          <ComplianceMetricCard
            label="Open items"
            value={openItems != null ? String(openItems) : "—"}
            progress={openItems != null ? Math.min(100, openItems * 10) : 0}
          />
          <ComplianceMetricCard
            label="Overdue"
            value={overdueCount != null ? String(overdueCount) : "—"}
            progress={overdueCount != null ? Math.min(100, overdueCount * 15) : 0}
          />
        </Box>
      )}
    </Box>
  );
}
